import React, { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
  children?: ReactNode;
  /** Optional fallback UI. If not provided, uses default error card. */
  fallback?: ReactNode;
  /** Optional name for logging (e.g., "ReviewPage", "Chessboard") */
  name?: string;
  /** If true, shows a compact inline error instead of full-page */
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary — catches rendering errors in child tree.
 *
 * Usage:
 *   <ErrorBoundary name="ReviewPage">
 *     <Review />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary inline name="EvalBar">
 *     <EvaluationBar ... />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const label = this.props.name || "ErrorBoundary";
    console.error(`[${label}] Uncaught error:`, error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Inline (compact) error
      if (this.props.inline) {
        return (
          <div style={inlineStyles.container}>
            <span style={inlineStyles.icon}>⚠</span>
            <span style={inlineStyles.message}>
              {this.state.error?.message || "Something went wrong"}
            </span>
            <button style={inlineStyles.retry} onClick={this.handleRetry}>
              Retry
            </button>
          </div>
        );
      }

      // Full error card (default)
      return (
        <div style={fullStyles.container}>
          <div style={fullStyles.card}>
            <div style={fullStyles.icon}>💥</div>
            <h2 style={fullStyles.title}>Something went wrong</h2>
            <p style={fullStyles.message}>
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            {this.props.name && (
              <p style={fullStyles.context}>Component: {this.props.name}</p>
            )}
            <div style={fullStyles.actions}>
              <button style={fullStyles.retryButton} onClick={this.handleRetry}>
                Try Again
              </button>
              <button
                style={fullStyles.reloadButton}
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const inlineStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "var(--color-error-muted, rgba(239, 68, 68, 0.15))",
    borderRadius: "6px",
    fontSize: "13px",
    color: "var(--color-text-secondary)",
  } as React.CSSProperties,
  icon: {
    fontSize: "14px",
    flexShrink: 0,
  } as React.CSSProperties,
  message: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  retry: {
    padding: "2px 10px",
    backgroundColor: "transparent",
    border: "1px solid var(--color-border)",
    borderRadius: "4px",
    color: "var(--color-text-secondary)",
    fontSize: "12px",
    cursor: "pointer",
    flexShrink: 0,
  } as React.CSSProperties,
};

const fullStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
    minHeight: "300px",
  } as React.CSSProperties,
  card: {
    backgroundColor: "var(--color-bg-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "12px",
    padding: "40px 32px",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center" as const,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  } as React.CSSProperties,
  icon: {
    fontSize: "36px",
    marginBottom: "16px",
  } as React.CSSProperties,
  title: {
    color: "var(--color-text-primary)",
    fontSize: "18px",
    fontWeight: 600,
    margin: "0 0 8px 0",
  } as React.CSSProperties,
  message: {
    color: "var(--color-text-secondary)",
    fontSize: "14px",
    lineHeight: 1.5,
    margin: "0 0 8px 0",
    wordBreak: "break-word" as const,
  } as React.CSSProperties,
  context: {
    color: "var(--color-text-tertiary)",
    fontSize: "12px",
    fontFamily: "monospace",
    margin: "0 0 20px 0",
  } as React.CSSProperties,
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
  } as React.CSSProperties,
  retryButton: {
    padding: "8px 20px",
    backgroundColor: "var(--color-accent)",
    border: "none",
    borderRadius: "6px",
    color: "var(--color-text-primary)",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  } as React.CSSProperties,
  reloadButton: {
    padding: "8px 20px",
    backgroundColor: "transparent",
    border: "1px solid var(--color-border)",
    borderRadius: "6px",
    color: "var(--color-text-secondary)",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
  } as React.CSSProperties,
};